/**
 * Helpers for deriving J-code issue labels (denial risks, initiation/continuation)
 * from auth-on-file entries and submission data.
 *
 * @module jCodeIssueLabels
 */
import {
  AuthOnFile,
  AuthRangeResponse,
  JCodeDetails,
} from "../../../data-model/medicalPaOrdersModel";
import {
  JCodeIssueLabel,
  JCodeStatusValue,
} from "../../../enums/jCodeIssueLabel";
import { Submission } from "../../../hooks/singleMedicalPaOrderDetails/useSubmissionInformation";

/**
 * Returns non–denial-risk issue labels for a single J-code (initiation/continuation, DX line,
 * NCCN guidelines met when applicable). Used by Medical Necessity Analysis and similar UIs;
 * not used for the CPT Description column icons.
 */
export function getJCodeLabels(jCode: JCodeDetails): string[] {
  const labels: string[] = [];
  const status = jCode?.initiation_continuation_status;
  if (status != null && String(status).trim() !== "") {
    status === JCodeStatusValue.CONTINUATION
      ? labels.push(JCodeIssueLabel.CONTINUATION)
      : labels.push(JCodeIssueLabel.INITIATION);
  }
  if (jCode?.j_code) {
    labels.push(`DX: ${jCode.j_code}`);
  }
  if (jCode?.nccn_match !== JCodeStatusValue.NO_MATCH) {
    labels.push(JCodeIssueLabel.NCCN_GUIDELINES_MET);
  }
  return labels;
}

/**
 * Returns only denial-risk labels for a single J-code (excludes Continuation/initiation).
 * Used for header denial-risk chips: when only these are present we show red badges; continuation-only shows no red badge.
 */
export function getJCodeDenialRiskLabels(jCode: JCodeDetails): string[] {
  const labels: string[] = [];
  if (jCode?.drug_preference === JCodeStatusValue.NON_PREFERRED) {
    labels.push(JCodeIssueLabel.NON_PREFERRED_DRUGS);
  }
  if (jCode?.nccn_match === JCodeStatusValue.NO_MATCH) {
    labels.push(JCodeIssueLabel.NCCN_GUIDELINES_NOT_MET);
  }
  return labels;
}

/**
 * Collects unique denial-risk labels across all J-codes in the order.
 * Used for header denial-risk chips: each label becomes its own badge when present.
 * Also flags MISSING_DX_CODE when submission's regimen ICD state is NO_MATCH.
 *
 * @param authOnFile - Auth-on-file record containing J-code entries
 * @param submissionData - Submission record for regimen ICD state check
 * @returns Deduplicated array of denial-risk label strings
 */
export function getAllOrderDenialRiskLabels(
  authOnFile?: AuthOnFile,
  submissionData?: Submission,
): string[] {
  if (!authOnFile?.entries && !authOnFile?.auth_entries) {
    return [];
  }
  const set = new Set<string>();
  const entries = (authOnFile.entries ??
    authOnFile.auth_entries) as AuthRangeResponse[];
  entries.forEach((entry) => {
    (entry.j_codes ?? []).forEach((j) => {
      getJCodeDenialRiskLabels(j).forEach((label) => set.add(label));
    });
  });
  if (submissionData?.regimen_icd_state === JCodeStatusValue.NO_MATCH) {
    set.add(JCodeIssueLabel.MISSING_DX_CODE);
  }
  return Array.from(set);
}
